/** @jsxImportSource @emotion/react */
import type { KeyboardEventHandler } from 'react';
import { useCallback, useContext, useMemo, useRef, useState } from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import {
  ChatActionType,
  type Chat,
  ChatResponseType,
  type KolSearchForm,
  type KolSearchResult,
} from '@trinity/chat';
import { SearchQueryHint } from '@trinity/chat-components/components/presentation/Message.QueryHint/index';
import {
  DispatchKolSearchFormContext,
  KolSearchFormContext,
  KolSearchIdentifierContext,
  KolSearchRecommendationsContext,
  SubmitActionContext,
} from './context';
import { KolSearchInputForm } from './KolSearch.Form';
import { KolResultsContainer, KolResultsTable } from './KolSearch.Table';
import { KolTableContainer } from './KolTableContainer';
import { messageLaneLeftPadding, messageLaneRightPadding, messageLaneWidth } from './Message.styles';
import { BasicSystemUserMessage } from './Message.System.Basic';

type Props = {
  queryIdentifier: string;
  response: Chat.QueryResponse<typeof ChatResponseType.HcpRecommendationsSearch>;
  disabled?: boolean;
};

export const HcpRecommendationsSearchResponse = ({ queryIdentifier, response, disabled = false }: Props) => {
  const submitAction = useContext(SubmitActionContext);

  const [formState, setFormState] = useState<Partial<KolSearchForm>>({
    kolId: null,
    name: '',
  });
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const inputFocused = useRef(false);

  const copy = getCopy();
  const recommendations = useMemo(() => ({
    status: 'loaded' as const,
    items: response.data.recommendations,
    total: response.data.total,
  }), [response.data.recommendations, response.data.total]);

  const handleSelectProfile = useCallback((item: { id: number; name: string }) => {
    if (disabled) {
      return;
    }

    submitAction(
      {
        type: ChatActionType.SelectHcpSubject,
        payload: {
          hcpId: item.id,
          hcpName: item.name,
        },
      },
      `Show me the profile for ${item.name}.`
    );
  }, [disabled, submitAction]);

  const handleFormUpdated = useCallback((form: Partial<KolSearchForm>) => {
    setFormState((prev) => ({ ...prev, ...form }));
    setErrorMessage(null);
  }, []);

  const handleSearchSubmit = useCallback(() => {
    if (disabled) {
      return;
    }

    const hcpId = formState.kolId;
    const hcpName = formState.name?.trim();

    if (typeof hcpId === 'number' && hcpName) {
      handleSelectProfile({ id: hcpId, name: hcpName });
      return;
    }

    const location = formState.location;
    const hasName = !!hcpName;
    const hasLocation = !!location;

    if (!hasName && !hasLocation) {
      setErrorMessage('Please select an HCP, enter a name, or provide a location before searching.');
      return;
    }

    submitAction(
      {
        type: ChatActionType.SearchHcps,
        payload: {
          ...(hasName ? { name: hcpName } : {}),
          ...(hasLocation
            ? {
                location: {
                  id: location.id,
                  latitude: location.latitude,
                  longitude: location.longitude,
                  name: location.name,
                },
              }
            : {}),
        },
      },
      hasName ? `Search for HCPs matching ${hcpName}.` : 'Search for HCPs near the selected location.'
    );
  }, [disabled, formState.kolId, formState.location, formState.name, handleSelectProfile, submitAction]);

  const submitDisabled = useMemo(() => {
    return disabled || (!formState.name && !formState.location);
  }, [disabled, formState.location, formState.name]);

  const handleInputBlur = useCallback(() => {
    inputFocused.current = false;
  }, []);

  const handleInputFocus = useCallback(() => {
    inputFocused.current = true;
  }, []);

  const handleKeyDown: KeyboardEventHandler<HTMLInputElement> = useCallback((e) => {
    if (!inputFocused.current || submitDisabled) {
      return;
    }

    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearchSubmit();
    }
  }, [handleSearchSubmit, submitDisabled]);

  const handleAutocompleteSelection = useCallback((item: KolSearchResult) => {
    handleSelectProfile({ id: item.id, name: item.name });
  }, [handleSelectProfile]);

  return (
    <MessageContainer>
      <KolTableContainer pageSize={25}>
        <BasicSystemUserMessage bodyStyles={css({ minWidth: messageLaneWidth, maxWidth: messageLaneWidth })}>
          <Body>
            {copy.header}

            <KolSearchFormContext.Provider value={formState}>
              <DispatchKolSearchFormContext.Provider value={handleFormUpdated}>
                <KolSearchRecommendationsContext.Provider value={recommendations}>
                  <KolSearchIdentifierContext.Provider value={queryIdentifier}>
                    <KolResultsContainer records={response.data.recommendations}>
                      <StyledRecommendationsTable
                        isInteractive={!disabled}
                        smartRoutingDisabled={true}
                        onSelectProfile={handleSelectProfile}
                      />
                    </KolResultsContainer>
                  </KolSearchIdentifierContext.Provider>
                </KolSearchRecommendationsContext.Provider>
              </DispatchKolSearchFormContext.Provider>
            </KolSearchFormContext.Provider>

            <Divider />
            {copy.alt}

            <StyledForm
              formState={formState}
              inputDisabled={disabled}
              onKeyDown={handleKeyDown}
              onInputFocus={handleInputFocus}
              onInputBlur={handleInputBlur}
              onKolSelect={handleAutocompleteSelection}
              setFormState={handleFormUpdated}
            />
            <StyledSearchQueryHint disabled={submitDisabled} onClick={handleSearchSubmit} value="Search for profiles." />
            {errorMessage ? <ErrorMessage>{errorMessage}</ErrorMessage> : null}
          </Body>
        </BasicSystemUserMessage>
      </KolTableContainer>
    </MessageContainer>
  );
};

function getCopy() {
  return {
    header: 'These are the potential HCPs that you can engage with in your territory.',
    alt: "Or if you're looking for a different HCP, get started with an HCP search.",
  };
}

const MessageContainer = styled.div({
  paddingLeft: messageLaneLeftPadding,
  paddingRight: messageLaneRightPadding,
});

const Body = styled.div({
  width: '100%',
});

const StyledRecommendationsTable = styled(KolResultsTable)({
  marginTop: 15,
  marginBottom: 15,
});

const StyledSearchQueryHint = styled(SearchQueryHint)({
  marginTop: 15,
});

const Divider = styled.div(({ theme }) => ({
  width: '100%',
  height: 2,
  backgroundColor: theme.palette.gray.light2,
  margin: '15px 0',
}));

const StyledForm = styled(KolSearchInputForm)({
  marginTop: 15,
  width: 500,
});

const ErrorMessage = styled.div(({ theme }) => ({
  marginTop: 10,
  color: theme.palette.error.main,
  fontFamily: theme.fonts.regular,
}));
